import React from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { Spinner, Button, Todo, Due, theme } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import HomeCard from './CardComponents/HomeCard'
import { useStockDashboardSummary } from '../../Residents/screens/ResidentEmarV2Screens/hooks/useStockDashboardSummary'
import { REPORTS } from '../../../components/routes/reports'

type RoundRow = {
  time: string
  status: 'not-signed' | 'due' | 'signed'
  dueCount: number
  notAdministered: number
  prn: number
}

const MedicationCardV2 = () => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const { data: stockData, isLoading: isLoadingStock } =
    useStockDashboardSummary()

    console.log('stockData', stockData)

  const mockRounds: RoundRow[] = [
    {
      time: '02:00',
      status: 'not-signed',
      dueCount: 0,
      notAdministered: 0,
      prn: 0
    },
    {
      time: '13:00',
      status: 'due',
      dueCount: 1,
      notAdministered: 0,
      prn: 0
    },
    {
      time: '15:00',
      status: 'due',
      dueCount: 0,
      notAdministered: 0,
      prn: 0
    }
  ]

  const isLoading = isLoadingStock
  const rounds = mockRounds

  const getStatusIcon = (status: RoundRow['status']) => {
    if (status === 'not-signed') {
      return <Todo />
    }
    if (status === 'due') {
      return <Due />
    }
    return null
  }

  const getStatusText = (status: RoundRow['status'], dueCount: number) => {
    if (status === 'not-signed') {
      return `${dueCount} ${t('NOT_SIGNED')}`
    }
    if (status === 'due') {
      return `${dueCount} ${t('DUE')}`
    }
    return t('SIGNED')
  }

  if (isLoading) {
    return (
      <HomeCard cardTitle={t('EMAR')}>
        <Spinner />
      </HomeCard>
    )
  }

  const hasRounds = rounds && rounds.length > 0
  const lowStockCount = stockData?.lowStockCount || 0
  const outOfStockCount = stockData?.outOfStockCount || 0
  const openDiscrepanciesCount =
    stockData?.openDiscrepanciesLast30DaysCount || 0
  const hasStockAlerts = lowStockCount > 0 || outOfStockCount > 0
  const hasAlerts = hasStockAlerts || openDiscrepanciesCount > 0

  return (
    <HomeCard cardTitle={t('EMAR')}>
      {hasRounds ? (
        <>
          <TableContainer>
            <Table>
              <thead>
                <TableHeaderRow>
                  <TableHeader>{t('TIME')}</TableHeader>
                  <TableHeader>{t('STATUS')}</TableHeader>
                  <TableHeader>{t('DETAILS')}</TableHeader>
                </TableHeaderRow>
              </thead>
              <tbody>
                {rounds.map((round, index) => (
                  <React.Fragment key={round.time}>
                    <TableRow>
                      <TableCell>{round.time}</TableCell>
                      <TableCell>
                        <StatusCell>
                          {getStatusIcon(round.status)}
                          <StatusText>
                            {round.status === 'signed' ? (
                              t('SIGNED')
                            ) : (
                              <>
                                <strong>{round.dueCount}</strong>{' '}
                                {round.status === 'not-signed'
                                  ? t('NOT_SIGNED')
                                  : t('DUE')}
                              </>
                            )}
                          </StatusText>
                        </StatusCell>
                      </TableCell>
                      <TableCell>
                        <DetailsCell>
                          <DetailText>
                            <strong>{round.notAdministered}</strong>{' '}
                            {t('NOT_ADMINISTERED')}
                          </DetailText>
                          <DetailText>
                            <strong>{round.prn}</strong> {t('PRN')}
                          </DetailText>
                        </DetailsCell>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </TableContainer>

          <FooterContainer>
            <Button variant="default">{t('VIEW_ALL')}</Button>
          </FooterContainer>

          {hasAlerts && (
            <AlertsContainer>
              {hasStockAlerts && (
                <AlertLink to={REPORTS.EMAR_STOCK_LEVELS}>
                  <Todo />
                  <AlertText>
                    {lowStockCount > 0 && outOfStockCount > 0
                      ? `${lowStockCount} ${t('LOW_STOCK')} ${t('AND')} ${outOfStockCount} ${t('OUT_STOCK')} ${t('MEDICATION')}`
                      : lowStockCount > 0
                        ? `${lowStockCount} ${t('LOW_STOCK')} ${t('AND')} ${outOfStockCount} ${t('OUT_STOCK')} ${t('MEDICATION')}`
                        : `${outOfStockCount} ${t('OUT_STOCK')} ${t('MEDICATION')}`}
                  </AlertText>
                </AlertLink>
              )}
              {openDiscrepanciesCount > 0 && (
                <AlertLink to={REPORTS.EMAR_STOCK_DISCREPANCIES}>
                  <Due color={theme.color.status.warning} />
                  <AlertText>
                    {openDiscrepanciesCount}{' '}
                    {t('OPEN_DISCREPANCIES_LAST_30_DAYS')}
                  </AlertText>
                </AlertLink>
              )}
            </AlertsContainer>
          )}
        </>
      ) : (
        <EmptyStateContainer>
          <EmptyStateText weight="bold">
            {t('ACCURATE_AND_EFFICIENT_EMAR_CHARTS')}
          </EmptyStateText>
          <EmptyStateText>
            {t('ACCOUNTABLE_EMAR_WITH_STOCK_CONTROL')}
          </EmptyStateText>
        </EmptyStateContainer>
      )}
    </HomeCard>
  )
}

const TableContainer = styled.div`
  width: 100%;
  margin-bottom: 4px;
`

const Table = styled.table`
  margin-top: 12px;
  width: 100%;
  border-collapse: collapse;
`

const TableHeaderRow = styled.tr``

const TableHeader = styled.th`
  text-align: left;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.neutral.color1};
  padding-right: 16px;

  &:last-child {
    padding-right: 0;
  }
`

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.color.neutral.color7};

  &:last-child {
    border-bottom: none;
  }
`

const TableCell = styled.td`
  padding: 8px 0;
  padding-right: 16px;
  font-size: 14px;
  color: ${({ theme }) => theme.color.neutral.color3};

  &:last-child {
    padding-right: 0;
  }
`

const StatusCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StatusText = styled(Text)`
  font-size: 14px;
  color: ${({ theme }) => theme.color.neutral.color4};
  font-weight: 400;

  strong {
    font-weight: 700;
  }
`

const DetailsCell = styled.div`
  display: flex;
  gap: 16px;
  justify-content: space-between;
`

const DetailText = styled(Text)`
  font-size: 14px;
  color: ${({ theme }) => theme.color.neutral.color4};
  font-weight: 400;

  strong {
    font-weight: 700;
  }
`

const FooterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`

const AlertsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const AlertLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    text-decoration-color: ${({ theme }) => theme.color.neutral.color4};
  }
`

const AlertText = styled(Text)`
  font-size: 14px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px 0;
`

const EmptyStateText = styled(Text)`
  font-size: 14px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

export default MedicationCardV2
